/**
 * Uploads one recording chunk: presigned PUT straight to S3 first, the Node /part route as fallback.
 *
 * - Direct: presign(meta) → { url, headers } then putToS3(url, headers, blob). S3 answers 412 when the object
 *   already exists (a retry of a chunk that did arrive), which counts as success.
 * - Retries (delays) apply to network errors, 429 and 5xx. A 4xx from presign (auth, size) is final: the
 *   server would refuse the fallback for the same reason.
 * - When the browser cannot reach S3 at all (network/CORS error, S3 error) and the fallback worked, direct
 *   upload is skipped for directCooldownMs so later chunks do not pay for failed attempts first.
 */
export function createChunkUploader({
  presign,
  putToS3,
  uploadViaServer,
  delays = [3000, 10000],
  directCooldownMs = 10 * 60 * 1000,
  directEnabled = true,
  sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
  now = () => Date.now(),
  jitter = () => Math.floor(Math.random() * 2000),
}) {
  let directSkippedUntil = 0;

  const retryable = (err) => !err.status || err.status === 429 || err.status >= 500 || err.fromS3;

  async function withRetries(fn) {
    let lastErr = null;
    for (let attempt = 0; attempt <= delays.length; attempt++) {
      try {
        return { ok: true, result: await fn() };
      } catch (err) {
        lastErr = err;
        if (!retryable(err) || attempt === delays.length) break;
        await sleep(delays[attempt] + jitter());
      }
    }
    return { ok: false, err: lastErr };
  }

  const direct = async (item) => {
    const p = await presign(item);
    if (!p || p.status !== 'success' || !p.url) {
      const e = new Error(p?.message || 'presign failed');
      e.status = 400;
      throw e;
    }
    return putToS3(p.url, p.headers || {}, item.blob);
  };

  /** Resolves { ok, via: 'direct' | 'server', duplicate?, err? }. Never throws. */
  return async function upload(item) {
    let directErr = null;
    if (directEnabled && now() >= directSkippedUntil) {
      const r = await withRetries(() => direct(item));
      if (r.ok) return { ok: true, via: 'direct', duplicate: r.result === 'duplicate' };
      directErr = r.err;
      // 404: server without /presign. Otherwise only network/S3 trouble justifies the fallback.
      if (directErr.status !== 404 && !retryable(directErr)) return { ok: false, via: 'direct', err: directErr };
    }
    const r = await withRetries(() => uploadViaServer(item));
    if (r.ok && directErr) directSkippedUntil = now() + directCooldownMs;
    return r.ok ? { ok: true, via: 'server', duplicate: !!r.result?.duplicate } : { ok: false, via: 'server', err: r.err };
  };
}
